/**
 * Admin → Stripe Connect (iter 84a)
 *
 * Lists every clinic + vendor user with their Stripe Connect onboarding
 * state and lets the admin:
 *   - Kick off onboarding for users that don't have a Connect account yet
 *     (opens Stripe's hosted onboarding flow in a new tab).
 *   - Refresh status (re-syncs `charges_enabled`/`payouts_enabled` from Stripe).
 *
 * Why a dedicated page? Connect onboarding is operator-facing only — no
 * clinic-portal or vendor-portal version yet. Clinics + vendors complete
 * onboarding via the hosted Stripe URL.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Building2,
  CheckCircle2,
  ExternalLink,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Stethoscope,
  XCircle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STATUS_PILL = {
  active: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
  onboarding: "bg-amber-500/15 border-amber-500/40 text-amber-200",
  rejected: "bg-rose-500/15 border-rose-500/40 text-rose-200",
  none: "bg-slate-700/40 border-slate-600/40 text-slate-300",
};

function StatusPill({ status, chargesEnabled, payoutsEnabled, userId }) {
  let label, key;
  if (!status) {
    label = "Not onboarded";
    key = "none";
  } else if (status === "active" && chargesEnabled && payoutsEnabled) {
    label = "Active";
    key = "active";
  } else if (status === "rejected") {
    label = "Rejected";
    key = "rejected";
  } else {
    label = chargesEnabled ? "Awaiting payouts" : "Onboarding";
    key = "onboarding";
  }
  return (
    <span
      data-testid={`stripe-connect-status-${userId}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_PILL[key]}`}
    >
      {key === "active" ? (
        <CheckCircle2 className="w-2.5 h-2.5" />
      ) : key === "rejected" ? (
        <XCircle className="w-2.5 h-2.5" />
      ) : (
        <ShieldCheck className="w-2.5 h-2.5" />
      )}
      {label}
    </span>
  );
}

export default function AdminStripeConnect() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState({});
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all"); // "all" | "clinic" | "vendor"

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/stripe-connect/users");
      const list = (r.data?.users || []).filter((u) =>
        ["clinic", "vendor"].includes(u.role)
      );
      setUsers(list);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnboard = async (user) => {
    setActioning((p) => ({ ...p, [user.id]: "onboarding" }));
    try {
      const origin = window.location.origin;
      const r = await api.post("/admin/stripe-connect/onboard", {
        user_id: user.id,
        return_url: `${origin}/admin/stripe-connect?onboarded=${user.id}`,
        refresh_url: `${origin}/admin/stripe-connect?refresh=${user.id}`,
      });
      toast.success("Opened Stripe onboarding in a new tab.");
      window.open(r.data.onboarding_url, "_blank", "noopener,noreferrer");
      // After the admin returns, they can click "Refresh".
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActioning((p) => ({ ...p, [user.id]: null }));
    }
  };

  const handleRefresh = async (user) => {
    setActioning((p) => ({ ...p, [user.id]: "refresh" }));
    try {
      const r = await api.post(`/admin/stripe-connect/refresh/${user.id}`);
      const updated = r.data;
      setUsers((curr) =>
        curr.map((u) =>
          u.id === user.id
            ? {
                ...u,
                stripe_connect_status: updated.stripe_connect_status,
                stripe_connect_charges_enabled: updated.charges_enabled,
                stripe_connect_payouts_enabled: updated.payouts_enabled,
              }
            : u
        )
      );
      toast.success(`Status: ${updated.stripe_connect_status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActioning((p) => ({ ...p, [user.id]: null }));
    }
  };

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const hay = `${u.name || ""} ${u.email || ""} ${u.clinic_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Admin · Payments
            </div>
            <h1 className="mt-1 text-3xl font-bold">Stripe Connect</h1>
            <p className="mt-2 text-sm text-slate-400 max-w-3xl">
              Onboard clinics + vendors into Stripe Connect (Express). Once a clinic is{" "}
              <span className="text-emerald-300 font-semibold">Active</span>, patients can pay for that clinic's
              flagship programs and funds split automatically to the clinic + direct vendors + Nodera fee. Vendors
              with direct-split or pharmacy-direct services must also be onboarded so payments can route to them.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="stripe-connect-refresh-list"
            className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold px-3 py-1.5 text-slate-200 hover:bg-slate-700"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh list
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / email / clinic"
            data-testid="stripe-connect-search"
            className="flex-1 min-w-[260px] bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-100"
          />
          {["all", "clinic", "vendor"].map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setRoleFilter(k)}
              data-testid={`stripe-connect-filter-${k}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                roleFilter === k
                  ? "bg-blue-600 text-white border-blue-500"
                  : "bg-slate-900 border-slate-800 text-slate-300 hover:bg-slate-800"
              }`}
            >
              {k === "all" ? "All" : k === "clinic" ? "Clinics" : "Vendors"}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading users…</div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="stripe-connect-empty"
            className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/30 p-12 text-center"
          >
            <div className="text-base font-semibold text-slate-200">No clinic or vendor users match</div>
            <p className="mt-2 text-sm text-slate-400">
              Adjust your filters above, or invite clinics/vendors from their respective admin pages.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
            <table className="w-full text-sm" data-testid="stripe-connect-table">
              <thead className="bg-slate-900/80 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Connect status</th>
                  <th className="text-left px-4 py-3">Account ID</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.map((u) => {
                  const acct = u.stripe_connect_account_id;
                  const status = u.stripe_connect_status;
                  const isActioning = !!actioning[u.id];
                  const RoleIcon = u.role === "clinic" ? Stethoscope : Building2;
                  return (
                    <tr key={u.id} data-testid={`stripe-connect-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <RoleIcon className="w-4 h-4 text-slate-500" />
                          <div>
                            <div className="font-semibold text-slate-100">
                              {u.name || u.clinic_name || u.email}
                            </div>
                            <div className="text-[11px] text-slate-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 capitalize text-slate-300">{u.role}</td>
                      <td className="px-4 py-3">
                        <StatusPill
                          status={status}
                          chargesEnabled={u.stripe_connect_charges_enabled}
                          payoutsEnabled={u.stripe_connect_payouts_enabled}
                          userId={u.id}
                        />
                      </td>
                      <td className="px-4 py-3 text-[11px] text-slate-500 font-mono">
                        {acct || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          {acct && (
                            <button
                              type="button"
                              disabled={isActioning}
                              onClick={() => handleRefresh(u)}
                              data-testid={`stripe-connect-refresh-${u.id}`}
                              className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-[11px] font-semibold px-2.5 py-1 text-slate-200 hover:bg-slate-700 disabled:opacity-50"
                            >
                              {actioning[u.id] === "refresh" ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3 h-3" />
                              )}
                              Refresh
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={isActioning}
                            onClick={() => handleOnboard(u)}
                            data-testid={`stripe-connect-onboard-${u.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-[11px] font-semibold px-2.5 py-1 hover:bg-blue-700 disabled:opacity-50"
                          >
                            {actioning[u.id] === "onboarding" ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <ExternalLink className="w-3 h-3" />
                            )}
                            {acct ? "Continue onboarding" : "Onboard"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-8 rounded-2xl border border-amber-500/20 bg-amber-950/20 p-4 text-xs text-amber-200/85 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-amber-400 mt-0.5 flex-none" />
          <div>
            <div className="font-semibold text-amber-200 mb-1">Sandbox mode</div>
            This connects to Stripe&apos;s test environment (<code>sk_test_emergent</code>). Onboarding redirects to
            Stripe&apos;s hosted test flow — use test SSN <code>000-00-0000</code> + test bank{" "}
            <code>routing 110000000 / account 000123456789</code>. Real-money rollout flips to the live API key once
            production onboarding kicks off.
          </div>
        </div>
      </div>
    </div>
  );
}
